/**
 * useBackendHealth — polls the backend /health endpoint and exposes
 * a coarse connection status to UI components.
 *
 * Returns one of three states:
 *   'connecting'  — initial mount, no result yet
 *   'healthy'     — last poll succeeded
 *   'unreachable' — at least 2 consecutive polls failed
 *
 * Uses 2 consecutive failures (~30s with the 15s interval) before
 * promoting to 'unreachable' so a single transient blip does not
 * fire the banner. Recovers to 'healthy' on the first successful
 * response. Polls in the background so an idle tab still notices
 * an outage.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../lib/apiClient';

const POLL_INTERVAL_MS = 15_000;
const TICK_INTERVAL_MS = 5_000;
const FAILURE_THRESHOLD = 2;

export function useBackendHealth() {
  const [status, setStatus] = useState('connecting');
  const [lastSuccessAt, setLastSuccessAt] = useState(null);
  const failuresRef = useRef(0);

  const query = useQuery({
    queryKey: ['backend-health'],
    queryFn: async () => {
      // /api/ping (not /health). Render's edge intercepts the /health
      // path during backend cold-starts and returns generic 200/502
      // responses WITHOUT CORS headers, which surfaces in the browser
      // as a misleading "No 'Access-Control-Allow-Origin' header"
      // error. /api/ping is on a non-platform-conventional path so it
      // reaches Express, where CORS attaches properly.
      return apiClient.get('/api/ping');
    },
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    retry: false,
    staleTime: 0,
    gcTime: Infinity,
  });

  useEffect(() => {
    if (query.isSuccess) {
      failuresRef.current = 0;
      setLastSuccessAt(query.dataUpdatedAt ? new Date(query.dataUpdatedAt) : new Date());
      setStatus('healthy');
    } else if (query.isError) {
      failuresRef.current += 1;
      if (failuresRef.current >= FAILURE_THRESHOLD) {
        setStatus('unreachable');
      }
    }
  }, [query.dataUpdatedAt, query.errorUpdatedAt, query.isSuccess, query.isError]);

  // Live tick so the "seconds since" display in the banner updates while unreachable.
  const [, forceTick] = useState(0);
  useEffect(() => {
    if (status !== 'unreachable') return undefined;
    const id = setInterval(() => forceTick(t => t + 1), TICK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [status]);

  const secondsSinceLastSuccess = lastSuccessAt
    ? Math.floor((Date.now() - lastSuccessAt.getTime()) / 1000)
    : null;

  return {
    status,
    lastSuccessAt,
    secondsSinceLastSuccess,
    consecutiveFailures: failuresRef.current,
  };
}

export default useBackendHealth;
